import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Progress, Button, message } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { AssessmentQuestions } from './AssessmentQuestions';
import moment from 'moment';
import { SET_ASSESSMENT_RESULTS } from '../../ducks/Assessment/constants';
import Lifestyle from '../../assets/images/lifestyle.svg';
import './index.css';
import questionsObj, { Units } from './questionsObj';
import {
  assessmentUser,
  getAssessmentProgressUser,
  showAuthModal,
  logOut,
} from '../../ducks/Auth/actions';
import { clearProfile, saveProfile } from '../../ducks/Profile/actions';
import { AssessmentLoader } from './AssessmentLoader';
import { signOut } from '../../helpers/auth';
import { clearRecipes } from '../../ducks/Recipe/actions';
import {
  ASSESSMENT_TYPES,
  getQuestionsForType,
} from '../../config/assessmentConfigs';
import {
  fetchAssessmentByType,
  fetchAssessmentById,
} from '../../services/assessmentApi';

class ShortAssessment extends React.Component {
  constructor(props) {
    super(props);
    const {
      assessmentType,
      assessmentQuestions,
      totalQuestions,
      initialProgress,
      initialActiveQuestion,
    } = this.getAssessmentConfig(props);
    this.state = {
      progress: initialProgress,
      responseObj: {
        assessmentStep: initialActiveQuestion,
        weightTraningexprience: {
          key: 1,
          text: 'Beginner',
          value: 'Beginner',
          info: "I've never lifted weights, or just recently started.",
        },
      },
      totalQuestions,
      displayTotal: totalQuestions,
      assessmentType,
      loadingCalculation: false,
      assessmentQuestions,
      errors: [],
      units: Units.imperial,
      activeQuestion: initialActiveQuestion,
      minCaloriesDay: 0,
      confirmModalVisible: false,
      loading: true,
      assessmentSubmitLoader: true,
      assessmentFromApi: null,
      assessmentLoadError: null,
    };
  }

  getAssessmentConfig = (props = this.props) => {
    const assessmentType =
      props?.auth?.user?.assessmentType || ASSESSMENT_TYPES.FULL;
    // Drop the two nutrition-only questions for a client whose trainer disabled
    // nutrition. The server already strips them from custom assessments; this
    // also covers the client-built Full/Basic presets. Absent ⇒ on.
    const nutritionOn =
      props?.auth?.user?.enabledModules?.nutrition !== false;
    const apiAssessment = this.state?.assessmentFromApi;
    if (apiAssessment) {
      const assessmentQuestions = getQuestionsForType(
        assessmentType,
        questionsObj,
        null,
        apiAssessment,
        nutritionOn
      );
      const totalQuestions = Object.keys(assessmentQuestions).length;
      const initialActiveQuestion = totalQuestions > 0 ? 1 : 0;
      const initialProgress =
        totalQuestions > 0 ? (initialActiveQuestion / totalQuestions) * 100 : 100;
      return {
        assessmentType,
        assessmentQuestions,
        totalQuestions,
        initialProgress,
        initialActiveQuestion,
      };
    }
    const assessmentQuestions = getQuestionsForType(
      assessmentType,
      questionsObj,
      null,
      null,
      nutritionOn
    );
    const totalQuestions = Object.keys(assessmentQuestions).length;
    const initialActiveQuestion = totalQuestions > 0 ? 1 : 0;
    const initialProgress =
      totalQuestions > 0 ? (initialActiveQuestion / totalQuestions) * 100 : 100;
    return {
      assessmentType,
      assessmentQuestions,
      totalQuestions,
      initialProgress,
      initialActiveQuestion,
    };
  };

  applyAssessmentConfig = (props = this.props) => {
    const { assessmentType, assessmentQuestions, totalQuestions } =
      this.getAssessmentConfig(props);
    this.setState((prevState) => {
      const hasQuestions = totalQuestions > 0;
      const fallbackActive = hasQuestions ? 1 : 0;
      const previousActive =
        prevState.activeQuestion > 0
          ? prevState.activeQuestion
          : fallbackActive;
      const activeQuestion = hasQuestions
        ? Math.min(previousActive, totalQuestions)
        : 0;
      const responseAssessmentStep =
        prevState.responseObj && prevState.responseObj.assessmentStep
          ? Math.min(prevState.responseObj.assessmentStep, totalQuestions)
          : activeQuestion;
      const progress = this.calculateProgressPercentage(
        responseAssessmentStep || activeQuestion,
        totalQuestions
      );

      return {
        assessmentType,
        assessmentQuestions,
        totalQuestions,
        displayTotal: totalQuestions,
        activeQuestion,
        progress,
        responseObj: {
          ...prevState.responseObj,
          assessmentStep: responseAssessmentStep,
        },
      };
    });
  };

  calculateProgressPercentage = (
    step,
    totalQuestions = this.state.totalQuestions
  ) => {
    if (!totalQuestions || totalQuestions <= 0) {
      return 100;
    }
    const normalizedStep = Math.min(Math.max(step, 0), totalQuestions);
    return (normalizedStep / totalQuestions) * 100;
  };
  componentDidMount(prevProps, prevState) {
    this.props.getAssessmentProgressUser();
    this.loadAssessmentFromApiIfNeeded();
  }

  loadAssessmentFromApiIfNeeded = () => {
    const user = this.props.auth?.user;
    if (!user) return;
    if (this.state.assessmentFromApi || this.state.assessmentLoadError) return;

    const assessmentType = user.assessmentType || ASSESSMENT_TYPES.FULL;
    if (assessmentType === ASSESSMENT_TYPES.NONE) return;

    const fetchPromise =
      assessmentType === ASSESSMENT_TYPES.FULL ||
      assessmentType === ASSESSMENT_TYPES.BASIC
        ? fetchAssessmentByType(assessmentType)
        : assessmentType === ASSESSMENT_TYPES.CUSTOM && user.customAssessmentId
          ? fetchAssessmentById(user.customAssessmentId)
          : null;

    if (!fetchPromise) return;

    fetchPromise
      .then((assessment) => {
        this.setState({ assessmentFromApi: assessment }, () =>
          this.applyAssessmentConfig()
        );
      })
      .catch((err) => {
        console.warn('Assessment API load failed, using frontend config', err);
        this.setState({ assessmentLoadError: err });
      });
  };

  componentDidUpdate(prevProps, prevState) {
    const prevAssessmentType =
      prevProps?.auth?.user?.assessmentType || ASSESSMENT_TYPES.FULL;
    const currentAssessmentType =
      this.props?.auth?.user?.assessmentType || ASSESSMENT_TYPES.FULL;
    if (prevAssessmentType !== currentAssessmentType) {
      this.applyAssessmentConfig();
    }

    // When user becomes available, try to load assessment from API if we haven't yet
    if (
      this.props.auth?.user &&
      !prevProps.auth?.user &&
      !this.state.assessmentFromApi &&
      !this.state.assessmentLoadError
    ) {
      this.loadAssessmentFromApiIfNeeded();
    }
    // When API assessment just loaded, apply config
    if (
      this.state.assessmentFromApi &&
      prevState.assessmentFromApi !== this.state.assessmentFromApi
    ) {
      this.applyAssessmentConfig();
    }

    if (prevProps.loading !== this.props.loading) {
      this.setState({
        loading: this.props.loading,
      });
    }
    if (
      prevProps.assessmentSubmitLoader !== this.props.assessmentSubmitLoader &&
      this.props.assessmentSubmitLoader
    ) {
      this.setState({ loadingCalculation: this.props.assessmentSubmitLoader });
    }
    // }
    // if (!this.props.auth.assessmentProgress) {
    //   this.setState({
    //     loading: false
    //   })
    // }
    if (
      this.props.auth.isAuthenticated &&
      this.props.auth.user &&
      this.props.auth.user.isAssessmentCompleted
    ) {
      // If completing fresh (loadingCalculation just finished), the
      // loadingCalculation branch below also fires — but this one fires
      // first due to auth update order. Always route through the welcome
      // screen on completion; the route guard handles direct-URL visits.
      const alreadyOnWelcome = this.props.history.location?.pathname === '/assessment-completed';
      if (!alreadyOnWelcome) {
        this.props.history.push({
          pathname: '/assessment-completed',
          state: { fromAssessment: true },
        });
      }
    }

    // `isAuthenticated` starts as null ("auth not checked yet") and only becomes
    // false once it has definitively resolved as logged-out. Using `=== false`
    // (not `!isAuthenticated`, which is also true for null) prevents bouncing to
    // "/" during the refresh auth-bootstrap window — the cause of the brief
    // login/landing flash before the assessment re-renders.
    if (this.props.auth.isAuthenticated === false && !this.props.loading) {
      this.props.history.push({
        pathname: '/',
      });
    }
    if (prevState.activeQuestion !== this.state.activeQuestion) {
      window.scrollTo(0, 0);
    }
    if (
      this.props.auth.assessmentProgress &&
      Object.keys(this.props.auth.assessmentProgress).length &&
      prevProps.responseObj !== this.props.auth.assessmentProgress
    ) {
      const totalQuestions = this.state.totalQuestions;
      const rawStep = this.props.auth.assessmentProgress.assessmentStep;
      const numericStep = Number.isFinite(rawStep)
        ? rawStep
        : Number(rawStep) || 0;
      const clampedStep =
        totalQuestions > 0
          ? Math.min(Math.max(numericStep, 0), totalQuestions)
          : 0;
      this.setState({
        responseObj: {
          ...this.state.responseObj,
          ...this.props.auth.assessmentProgress,
          assessmentStep: clampedStep,
        },
        activeQuestion: clampedStep,
        progress: this.calculateProgressPercentage(clampedStep),
        loading: false,
      });
    }
    if (
      this.state.loadingCalculation === false &&
      prevState.loadingCalculation !== this.state.loadingCalculation &&
      this.state.errors.length === 0
    ) {
      // if (this.props.auth && !this.props.auth.isAuthenticated) {
      //   this.props.setAuthVisible(true);
      // }
      // The line below is commented to prevent redirection to the results page when the user completes the form.
      // this.props.history.push("/assessment/results");

      // Dispatch a show modal action
      this.props.history.push({
        pathname: '/assessment-completed',
        state: { fromAssessment: true },
      });
      // this.props.showAuthModal({
      //   authRedirectRoute: "/dashboard/home",
      // });
    }
    const macrosEmpty =
      this.state.responseObj.foodPreference &&
      this.state.assessmentQuestions &&
      this.state.assessmentQuestions.macros &&
      (!this.state.assessmentQuestions.macros.options ||
        !this.state.assessmentQuestions.macros.options.length);
    if (
      this.state.responseObj.foodPreference !==
        prevState.responseObj.foodPreference ||
      (macrosEmpty &&
        this.state.assessmentQuestions !== prevState.assessmentQuestions)
    ) {
      // Derive macros options from foodPreference on change — and also re-derive
      // when foodPreference was restored after a page refresh and the config
      // rebuild reset macros.options (otherwise the macros step shows no options).
      this.macrosOptions();
    }
  }

  componentWillUnmount() {
    this.setState({ responseObj: {} });
  }
  // MM rework it. You dont need activeQuestion in state, you are using only "key" out of it
  handleProgress = (e, key, type) => {
    const { activeQuestion, totalQuestions } = this.state;
    if (!totalQuestions || totalQuestions <= 0) {
      return;
    }
    // also you dont need "e" as first argument in this function as you can decide
    // this part on activeQuestion.key here to calculateResults
    // The last step submits — but ONLY for a forward action. A Back click must
    // navigate (the Back button now renders on the final/password step), not
    // run calculateResults, which would otherwise fire the submit path on every
    // Back press from that screen.
    if (activeQuestion === totalQuestions && key !== 'back') {
      // if (e.target.innerText === "Submit") {
      if (this.state.errors.length === 0) {
        this.handleLoading();
        this.calculateResults(this.state.responseObj);
      }
    } else {
      let progress;
      let newActive;
      if (key === 'back') {
        newActive = this.state.activeQuestion - 1;
      } else {
        newActive = this.state.activeQuestion + 1;
      }
      this.setState({
        responseObj: {
          ...this.state.responseObj,
          assessmentStep: Math.min(newActive, totalQuestions),
        },
      });
      const nextActive = Math.min(newActive, totalQuestions);
      progress = this.calculateProgressPercentage(nextActive);
      // if (this.state.errors.length === 0 && newActive !== this.state.totalQuestions) {
      if (this.state.errors.length === 0) {
        // this.handleLoading();
        // this.calculateResults(this.state.responseObj);
        this.props.assessmentUser({
          ...this.state.responseObj,
          assessmentStep: nextActive,
        });
      }
      this.setState({
        progress,
        activeQuestion: nextActive,
      });
    }
  };

  handleChange = (e, type, measure) => {
    let responseObj = { ...this.state.responseObj };
    let assessmentQuestions = { ...this.state.assessmentQuestions };
    let date;
    let value;
    let measureSystem =
      (measure && measure === 'kg') || measure === 'cm' ? 'metric' : 'imperial';
    if (type === 'heightCm') {
      responseObj.measureForTabs = 'metric';
    } else if (type === 'heightIn') {
      responseObj.measureForTabs = 'imperial';
    }
    responseObj.assessmentStep = this.state.activeQuestion;
    // set default value
    if (!responseObj.Tabs) {
      responseObj.Tabs = {
        key: 2,
        text: 'Moderate',
        value: 'moderate',
        info: Units.imperial ? '~1lb' : '~.5kg',
        active: true,
      };
    }

    if (type === 'birthday') {
      const date = moment(e);
      let formattedDate = date.format('MM/DD/YYYY');
      value = { value: formattedDate };
    } else if (type === 'Custom') {
      if (!isNaN(+e.target.value)) {
        responseObj.macros.macros[measure] = +e.target.value;
      }
    } else if (type === 'byWhen') {
      let date = moment(e).toDate();
      let formattedDate = moment(date).format('MM/DD/YYYY');
      value = { value: formattedDate };
    } else if (e.hasOwnProperty('target') && measure) {
      value = {
        value: e.target.value,
        length: e.target.value.length,
        measure: measureSystem,
      };
    } else if (e.hasOwnProperty('target')) {
      value = { value: e.target.value, length: e.target.value.length };
    } else if (type === 'Tabs') {
      assessmentQuestions.goalWeight.component[1].options.forEach(
        (option) => delete option.active
      );
      e.active = true;
      value = e;
      responseObj[type] = value;
      delete responseObj.byWhen;
      this.setState({
        minCaloriesDay: this.calculateResults(responseObj, true),
      });
    } else if (type == 'heightFt' || type == 'heightIn') {
      value = { value: e, length: e.length };
    } else if (
      type == 'riskFollowing' ||
      type == 'cardioType' ||
      type == 'equipment' ||
      // Any answer carrying selectedOptions is a multi-select — slug-agnostic,
      // so custom multi-selects work whether keyed by slug or legacy custom_*.
      (e && Array.isArray(e.selectedOptions))
    ) {
      let { selectedOptions = [], otherDescription = null } = e;
      selectedOptions = selectedOptions.filter((v) => v != '');
      if (!selectedOptions.includes('Other')) {
        otherDescription = null;
      }
      value = {
        value: { selectedOptions, otherDescription },
        length: selectedOptions.length,
      };
    } else if (type == 'weightTraningexprience') {
      value = e;
    } else if (type == 'currentLocation') {
      value = e; // set e to value, here e has object{lat, lng, address}
    } else {
      const question = assessmentQuestions[type];
      if (question && Array.isArray(question.options)) {
        question.options.forEach((option) => delete option.active);
      }
      e.active = true;
      value = e;
    }
    responseObj[type] = value;
    if (type === 'birthday') {
      const age = moment().diff(moment(e), 'years');
      responseObj.age = {
        value: age,
        length: String(age).length,
      };
    } else if (type === 'goalWeight') {
      delete responseObj.byWhen;
      this.setState({
        minCaloriesDay: this.calculateResults(responseObj, true),
      });
    }
    this.setState({
      responseObj,
      assessmentQuestions,
    });
  };

  handleLoading = () => {
    this.setState({ loadingCalculation: true });
    // setTimeout(() => {
    //   this.setState({ loadingCalculation: false });
    // }, 2000);
  };

  macrosOptions = () => {
    let responseObj = { ...this.state.responseObj };
    let assessmentQuestions = { ...this.state.assessmentQuestions };
    if (responseObj.hasOwnProperty('foodPreference')) {
      if (
        responseObj.foodPreference.value === 'anything' ||
        responseObj.foodPreference.value === 'vegetarian'
      ) {
        assessmentQuestions.macros.options = [
          { text: 'Recommended', macros: { protein: 30, carb: 35, fat: 35 } },
          { text: 'Low fat', macros: { protein: 30, carb: 50, fat: 20 } },
          { text: 'Low carb', macros: { protein: 30, carb: 20, fat: 50 } },
          { text: 'Custom', macros: { protein: 25, carb: 25, fat: 50 } },
        ];
      } else if (responseObj.foodPreference.value === 'paleo') {
        assessmentQuestions.macros.options = [
          { text: 'Recommended', macros: { protein: 30, carb: 35, fat: 35 } },
          { text: 'Low carb', macros: { protein: 30, carb: 20, fat: 50 } },
          { text: 'Custom', macros: { protein: 25, carb: 25, fat: 50 } },
        ];
      } else if (responseObj.foodPreference.value === 'keto') {
        assessmentQuestions.macros.options = [
          { text: 'Recommended', macros: { protein: 20, carb: 10, fat: 70 } },
          { text: 'Custom', macros: { protein: 25, carb: 25, fat: 50 } },
        ];
      } else if (responseObj.foodPreference.value === 'vegan') {
        assessmentQuestions.macros.options = [
          { text: 'Recommended', macros: { protein: 30, carb: 50, fat: 20 } },
          { text: 'Custom', macros: { protein: 40, carb: 30, fat: 30 } },
        ];
      }
    }
    this.setState({ assessmentQuestions });
  };

  handleMeasureValue = (e) => {
    this.setState({
      units: e.target.value,
    });
  };

  calculateResults = (responseObj, onlyGetCalories) => {
    const getResponseValue = (key, fallback = null) => {
      const item = responseObj?.[key];
      if (item === undefined || item === null) {
        return fallback;
      }
      if (typeof item === 'object' && item.hasOwnProperty('value')) {
        return item.value;
      }
      return item;
    };

    // Required answers for the calorie/profile math. During the live calorie
    // preview (onlyGetCalories) — and defensively, if the form is ever
    // submitted before every question is answered — these can be missing. Bail
    // out instead of crashing on `undefined.value` (Sentry: "undefined is not
    // an object (evaluating 'e.gender.value')").
    const gender = getResponseValue('gender');
    const weight = getResponseValue('weight');
    const age = getResponseValue('age');
    const activityLevelVal = getResponseValue('activityLevel');
    const goalVal = getResponseValue('goal');
    const heightCm = getResponseValue('heightCm');
    const heightFt = getResponseValue('heightFt');
    const heightIn = getResponseValue('heightIn');
    const macros =
      responseObj && responseObj.macros && responseObj.macros.macros;
    // A nutrition-off client never sees the macros question (it's dropped from
    // their assessment), so don't hard-require it — default to the app's
    // balanced "Recommended" split (30/35/35), matching the server's
    // resolveMacroSplit default. Without this the completion dispatch below
    // never fires and the client is stuck on /assessment forever.
    const nutritionOn =
      this.props?.auth?.user?.enabledModules?.nutrition !== false;
    const macroSplit =
      macros || (!nutritionOn ? { protein: 30, carb: 35, fat: 35 } : null);
    const hasHeight =
      heightCm != null || (heightFt != null && heightIn != null);
    if (
      gender == null ||
      weight == null ||
      age == null ||
      activityLevelVal == null ||
      goalVal == null ||
      !hasHeight ||
      macroSplit == null
    ) {
      // For a real submit, clear the spinner so the user isn't stuck, and tell
      // them what's wrong instead of failing silently.
      if (!onlyGetCalories) {
        this.setState({ loadingCalculation: false });
        message.error(
          'Some answers are missing — please go back and complete every question, then submit again.'
        );
      }
      return null;
    }

    let nutrition = [];
    let cm =
      heightCm != null
        ? Number(heightCm)
        : Number(heightFt) * 30.48 + Number(heightIn) * 2.54;
    let kg =
      this.state.units === Units.imperial
        ? (Number(weight) / 2.205).toFixed(2)
        : Number(weight).toFixed(2);

    let bmr = Math.round(
      gender === 'male'
        ? 10 * Number(kg) + 6.25 * cm - 5 * Number(age) + 5
        : 10 * Number(kg) + 6.25 * cm - 5 * Number(age) - 161
    );

    let maintenanceCal = Math.round(bmr * Number(activityLevelVal));
    let calculatedGoalCalories = 0;
    let days = 0;

    //when goal is to lose weight or gain muscle
    // if (responseObj.hasOwnProperty("byWhen") || onlyGetCalories) {
    //   if (onlyGetCalories) {
    //     while (calculatedGoalCalories < 1200) {
    //       let currentWeight = Number(responseObj.weight.value);
    //       let goalWeight = Number(responseObj.goalWeight.value);
    //       if (goalWeight && (currentWeight > goalWeight)) {
    //         responseObj.goal = {
    //           goal: "lose",
    //           info: "If you are trying to lose 10 lbs or more.",
    //           key: 2,
    //           text: "Lose weight",
    //           value: "Lose weight"
    //         }
    //       } else if (goalWeight && (currentWeight < goalWeight)) {
    //         responseObj.goal = {
    //           goal: "gain",
    //           info: "Building muscle will require a surplus of calories. Note that you might add some body fat while building muscle.",
    //           key: 1,
    //           text: "Build muscle",
    //           value: "Build muscle"
    //         }
    //       }
    //       let goalPoundsOrKg =
    //         responseObj.goal.value === "Lose weight"
    //           ? Number(currentWeight - goalWeight)
    //           : Number(goalWeight - currentWeight);
    //       let goalPoundsOrKgCalories;
    //       switch (responseObj.Tabs.value) {
    //         case 'slow':
    //           goalPoundsOrKgCalories = responseObj.measureForTabs === 'imperial'
    //             ? goalPoundsOrKg * 1750 //~0.5lb per week
    //             : goalPoundsOrKg * 1925; //~0.25 kg per week
    //           break;
    //         case 'moderate':
    //           goalPoundsOrKgCalories = responseObj.measureForTabs === 'imperial'
    //             ? goalPoundsOrKg * 3500 //~1 lb per week
    //             : goalPoundsOrKg * 3850; //~0.5 kg per week
    //           break;
    //         case 'aggressive':
    //           goalPoundsOrKgCalories = responseObj.measureForTabs === 'imperial'
    //             ? goalPoundsOrKg * 7000 //~2lb per week
    //             : goalPoundsOrKg * 7700; //~1kg per week
    //           break;
    //       }
    //       // let goalPoundsOrKgCalories = responseObj.hasOwnProperty("heightCm")
    //       //   ? goalPoundsOrKg * 7000 //making 7000 the calorie needed to gain or lose 1kg
    //       //   : goalPoundsOrKg * 2500; //making 2500 the calorie needed to gain or lose 1lb
    //       if (responseObj.goal.value === "Lose weight") {
    //         let deficitPerDay = Math.round(goalPoundsOrKgCalories / days);
    //         calculatedGoalCalories = maintenanceCal - deficitPerDay;
    //       } else if (responseObj.goal.value === "Build muscle") {
    //         let surplusPerDay = Math.round(goalPoundsOrKgCalories / days);
    //         calculatedGoalCalories = maintenanceCal + surplusPerDay;
    //       }
    //       days++
    //     }
    //     return days
    //   }

    //   if (responseObj.hasOwnProperty("byWhen")) {
    //     let goalDate = responseObj.byWhen.value;
    //     let today = moment(new Date()).toDate();
    //     let formattedToday = moment(today).format("MM/DD/YYYY");
    //     days = Math.abs(
    //       moment(formattedToday, "MM/DD/YYYY")
    //         .startOf("day")
    //         .diff(moment(goalDate, "MM/DD/YYYY").startOf("day"), "days")
    //     );
    //   }

    //   let currentWeight = Number(responseObj.weight.value);
    //   let goalWeight = Number(responseObj.goalWeight.value);
    //   if (goalWeight && (currentWeight > goalWeight)) {
    //     responseObj.goal = {
    //       goal: "lose",
    //       info: "If you are trying to lose 10 lbs or more.",
    //       key: 2,
    //       text: "Lose weight",
    //       value: "Lose weight"
    //     }
    //   } else if (goalWeight && (currentWeight < goalWeight)) {
    //     responseObj.goal = {
    //       goal: "gain",
    //       info: "Building muscle will require a surplus of calories. Note that you might add some body fat while building muscle.",
    //       key: 1,
    //       text: "Build muscle",
    //       value: "Build muscle"
    //     }
    //   }
    //   let goalPoundsOrKg =
    //     responseObj.goal.value === "Lose weight"
    //       ? Number(currentWeight - goalWeight)
    //       : Number(goalWeight - currentWeight);
    //   let goalPoundsOrKgCalories;
    //   switch (responseObj.Tabs.value) {
    //     case 'slow':
    //       goalPoundsOrKgCalories = responseObj.measureForTabs === 'imperial'
    //         ? goalPoundsOrKg * 1750 //~0.5lb per week
    //         : goalPoundsOrKg * 1925; //~0.25 kg per week
    //       break;
    //     case 'moderate':
    //       goalPoundsOrKgCalories = responseObj.measureForTabs === 'imperial'
    //         ? goalPoundsOrKg * 3500 //~1 lb per week
    //         : goalPoundsOrKg * 3850; //~0.5 kg per week
    //       break;
    //     case 'aggressive':
    //       goalPoundsOrKgCalories = responseObj.measureForTabs === 'imperial'
    //         ? goalPoundsOrKg * 7000 //~2lb per week
    //         : goalPoundsOrKg * 7700; //~1kg per week
    //       break;
    //   }
    //   // let goalPoundsOrKgCalories = responseObj.hasOwnProperty("heightCm")
    //   //   ? goalPoundsOrKg * 7000 //making 7000 the calorie needed to gain or lose 1kg
    //   //   : goalPoundsOrKg * 2500; //making 2500 the calorie needed to gain or lose 1lb
    //   if (responseObj.goal.value === "Lose weight") {
    //     let deficitPerDay = Math.round(goalPoundsOrKgCalories / days);
    //     calculatedGoalCalories = maintenanceCal - deficitPerDay;
    //   } else if (responseObj.goal.value === "Build muscle") {
    //     let surplusPerDay = Math.round(goalPoundsOrKgCalories / days);
    //     calculatedGoalCalories = maintenanceCal + surplusPerDay;
    //   }
    // }

    //When goal is other than losing weight or gaining muscle
    // if (responseObj.goal.value === "Athletic performance") {
    //   let surplusPerDay = maintenanceCal * 0.1; //10% extra from maintenance
    //   calculatedGoalCalories = maintenanceCal + surplusPerDay;
    // } else
    if (goalVal === 'Lose body fat') {
      let deficitPerDay = maintenanceCal * 0.1;
      calculatedGoalCalories = maintenanceCal - deficitPerDay;
    } else if (goalVal === 'Gain muscle') {
      let deficitPerDay = maintenanceCal * 0.1;
      calculatedGoalCalories = maintenanceCal + deficitPerDay;
    } else if (goalVal === 'Body recomposition') {
      let deficitPerDay = maintenanceCal * 0.1; //10% below maintenance
      calculatedGoalCalories = maintenanceCal - deficitPerDay;
    } else if (goalVal === 'Improve overall health') {
      calculatedGoalCalories = maintenanceCal;
    }

    delete responseObj.activityLevel.key;
    let profile = {
      age: Number(getResponseValue('age', 0)),
      birthday: getResponseValue('birthday'),
      gender: getResponseValue('gender'),
      height: cm,
      currentWeight: Number(responseObj.weight.value),
      startWeight: Number(responseObj.weight.value),
      activityLevel: responseObj.activityLevel,
      goal: responseObj.goal,
      foodPreference: getResponseValue('foodPreference'),
      // mealsPerDay: responseObj.mealsPerDay.value,
      // workoutLocation: responseObj.workoutLocation.value,
      macros: macroSplit,
      isMetric: responseObj.hasOwnProperty('heightCm') ? true : false,
      secondaryGoals: getResponseValue('secondaryGoals'),
      explainReachGoals: getResponseValue('explainReachGoals', ''),
      trainingActivity: getResponseValue('trainingActivity'),
      supplements: getResponseValue('supplements'),
      obstacleReachingFitnessGoals: getResponseValue(
        'obstacleReachingFitnessGoals'
      ),
      travel: getResponseValue('travel'),
      mealsPrefer: getResponseValue('mealsPrefer'),
      happyEatingFoods: getResponseValue('happyEatingFoods'),
      riskFollowing: getResponseValue('riskFollowing'),
      averageStepCount: getResponseValue('averageStepCount'),
      doStrengthTraining: getResponseValue('doStrengthTraining'),
      weightTraningexprience: getResponseValue('weightTraningexprience'),
      significantInjuries: getResponseValue('significantInjuries'),
      cardioType: getResponseValue('cardioType'),
      equipment: getResponseValue('equipment'),
      timeForWorkout: getResponseValue('timeForWorkout'),
      workoutFrequency: getResponseValue('workoutFrequency'),
      currentLocation: getResponseValue('currentLocation'),
      // currentLocation: responseObj.currentLocation.value,
      newPassword: getResponseValue('newPassword', ''),
    };

    // ToDo: make sure all values are converted before saving to DB. All values will be in metric and imperial conversion made on the fly
    if (responseObj.hasOwnProperty('byWhen')) {
      profile.byWhen = getResponseValue('byWhen');
      profile.rate = responseObj.Tabs || null;
      profile.goalWeight = Number(getResponseValue('goalWeight', 0));
    }

    if (profile.isMetric) {
      profile.height = Math.floor(cm);
    } else {
      profile.heightFt = Number(responseObj.heightFt.value);
      profile.heightIn = Number(responseObj.heightIn.value);
    }

    // Round-trip trainer-authored custom answers. Iterate the template's
    // custom-flagged questions (slug- or legacy custom_-keyed) rather than
    // scanning for a 'custom_' prefix, so slug-keyed answers are included too.
    // Without this they'd be dropped — only named fields are hand-copied above —
    // so custom answers would never reach the server / Profile.assessmentAnswers.
    // getResponseValue unwraps the {value} and multi-select
    // {value:{selectedOptions,otherDescription}} shapes.
    if (this.state.assessmentQuestions) {
      Object.entries(this.state.assessmentQuestions).forEach(([qKey, q]) => {
        if (q && q.custom === true && responseObj.hasOwnProperty(qKey)) {
          const value = getResponseValue(qKey);
          if (value !== undefined && value !== '') {
            profile[qKey] = value;
          }
        }
      });
    }

    let nutritionObj = {
      goal: responseObj.goal.goal,
      days: days,
      goalCalories: Math.round(calculatedGoalCalories),
      maintenanceCalories: Math.round(maintenanceCal),
      protein: {
        grams: Math.round(
          (calculatedGoalCalories * (macroSplit.protein / 100)) /
            4
        ),
        displayName: 'Protein',
      },
      carbs: {
        grams: Math.round(
          (calculatedGoalCalories * (macroSplit.carb / 100)) / 4
        ),
        displayName: 'Carbohydrates',
      },
      fats: {
        grams: Math.round(
          (calculatedGoalCalories * (macroSplit.fat / 100)) / 9
        ),
        displayName: 'Fats',
      },
    };
    const totalIntake =
      nutritionObj.carbs.grams +
      nutritionObj.protein.grams +
      nutritionObj.fats.grams;
    nutritionObj.carbs.percentage =
      (nutritionObj.carbs.grams / totalIntake) * 100;
    nutritionObj.protein.percentage =
      (nutritionObj.protein.grams / totalIntake) * 100;
    nutritionObj.fats.percentage =
      (nutritionObj.fats.grams / totalIntake) * 100;
    nutritionObj.totalIntake = totalIntake;
    nutrition.push(nutritionObj);
    this.props.setAssessmentResults(nutrition, this.state.responseObj, profile);
    if (
      this.state.responseObj.assessmentStep === this.state.totalQuestions ||
      this.state.activeQuestion === this.state.totalQuestions
    ) {
      this.props.assessmentUser({
        nutrition: nutritionObj,
        profile,
      });
    }
  };

  handleLogout = () => {
    signOut();
    this.props.clearRecipes();
    this.props.clearProfile();
    this.props.logOut();
    this.props.history.push('/');
  };

  renderCalculationCard = () => {
    return (
      <div className='content assessment'>
        <div className='start-calculation-card'>
          <div className='calculation-card-content'>
            <h1 className='title'>Personalized assessment</h1>
            <img className='img' src={Lifestyle} />
            <p className='blog-card-subtitle assessment-card-subtitle'>
              Not sure where to get started? You came to the right place! Get
              personalized recommendations based on your goal.
            </p>
            <Button
              size='large'
              type='primary'
              // onClick={() => this.setState({ calculationCard: false })}
            >
              Coming soon!
            </Button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    return (
      <React.Fragment>
        <div className='content short-assessment'>
          {this.state.loading ? (
            <AssessmentLoader />
          ) : (
            <>
              <div className='fixed-progress-bar'>
                <div className='back flex flex-center-aligned space-between'>
                  {this.state.totalQuestions > 0 &&
                  this.state.activeQuestion !== this.state.totalQuestions ? (
                    <span className='assessment-step-pill'>
                      Step {this.state.activeQuestion} of{' '}
                      {this.state.displayTotal}
                    </span>
                  ) : (
                    <span />
                  )}
                  <button
                    type='button'
                    className='assessment-logout-btn'
                    onClick={() => this.handleLogout()}
                  >
                    <LogoutOutlined />
                    Log out
                  </button>
                </div>
                {this.state.totalQuestions > 0 &&
                this.state.activeQuestion !== this.state.totalQuestions ? (
                  <Progress
                    strokeColor={{
                      '0%': 'var(--primary, #8b5cf6)',
                      '100%': '#06b6d4',
                    }}
                    percent={this.state.progress}
                    showInfo={false}
                  />
                ) : null}
              </div>
              {this.state.assessmentQuestions &&
                this.state.totalQuestions > 0 && (
                <AssessmentQuestions
                  {...this.props}
                  {...this.state}
                  activeQuestion={Object.values(
                    this.state.assessmentQuestions
                  ).find((val) => val.key === this.state.activeQuestion)}
                  handleMeasureValue={this.handleMeasureValue}
                  handleProgress={this.handleProgress}
                  handleChange={this.handleChange}
                  calculateResults={this.calculateResults}
                  handleLoading={this.handleLoading}
                />
              )}
            </>
          )}
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    loading: state.auth.loading,
    auth: state.auth,
    responseObj: state.auth.assessmentProgress,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    saveProfile: (responseObj) => {
      dispatch(saveProfile(responseObj));
    },
    setAssessmentResults: (nutrition, responseObj, profile) => {
      return dispatch({
        type: SET_ASSESSMENT_RESULTS,
        assessmentResults: nutrition,
        responseObj,
        profile,
      });
    },
    showAuthModal: (payload) => dispatch(showAuthModal(payload)),
    assessmentUser: (formData, cb) => dispatch(assessmentUser(formData, cb)),
    getAssessmentProgressUser: () => dispatch(getAssessmentProgressUser()),
    logOut: () => dispatch(logOut()),
    clearProfile: () => dispatch(clearProfile()),
    clearRecipes: () => dispatch(clearRecipes()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ShortAssessment));
